import { useEffect, useRef } from 'react'
import { useToast } from '@papan/core-ui/ui'

/**
 * Show the server's reason when a collaborative save is REFUSED.
 *
 * ── Why this exists as a shared hook ────────────────────────────────────
 *
 * A collab save travels over the Yjs WebSocket, so it reaches no Hono route,
 * no 413, and no `onError` middleware — the layers every other refusal in the
 * product is surfaced by. Before `saveError` the client learned only that the
 * save had not happened, and the explanation ("this drive is full…") stayed in
 * the server log. A user editing a document on a full drive watched saves
 * quietly stop working.
 *
 * Three editors share the same Yjs hook, so the surfacing lives here rather
 * than three times: two of them do not otherwise depend on `@papan/core-ui`,
 * and a copy each is how three toasts drift into saying three things about one
 * condition — the exact drift the shared `storageQuotaMessage` exists to stop.
 *
 * Renders the SERVER's sentence verbatim. It is already the same text upload,
 * paste and extract show, and inventing an editor-specific phrasing here would
 * make the same wall read differently depending on how you walked into it.
 */
export function useSaveRefusalToast(saveError: { message: string; nonce: number } | null): void {
  const toast = useToast()
  // Toast once per REFUSAL, not once per message.
  //
  // Keyed on the nonce rather than the sentence: a dirty document retries on a
  // debounce, so re-rendering the same refusal must not become a stream of
  // toasts — but two SEPARATE refusals carrying the same sentence must both be
  // shown. Deduping on the text got that backwards. Pressing "Overwrite Save"
  // after a full-drive refusal produced the identical string, React bailed out
  // of the state update, the effect never re-ran, and the user saw nothing —
  // on the retry the server calls "the LAST place that can afford to fail
  // silently".
  const lastShown = useRef<number | null>(null)

  useEffect(() => {
    if (!saveError) { lastShown.current = null; return }
    if (lastShown.current === saveError.nonce) return
    lastShown.current = saveError.nonce
    toast.error(saveError.message)
  }, [saveError, toast])
}
